import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { useSEO } from "@/hooks/useSEO";
import { Reveal } from "@/components/motion/Reveal";
import { MaturityBadge } from "@/components/MaturityBadge";
import { APPLICATIONS } from "@/config/site";

export default function ApplicationsPage() {
  const { t } = useLanguage();
  useSEO({ title: t.applications.seo.title, description: t.applications.seo.description, path: "/applications" });
  const a = t.applications;
  const published = APPLICATIONS.filter((app) => app.published);

  return (
    <div data-testid="applications-page" className="container-site py-14 sm:py-20 lg:py-28">
      <Reveal>
        <p className="eyebrow">{a.eyebrow}</p>
        <h1 className="mt-3 font-display text-4xl sm:text-5xl font-extrabold tracking-tight">{a.title}</h1>
        <p className="mt-5 max-w-2xl text-base sm:text-lg text-muted-foreground leading-relaxed">{a.intro}</p>
      </Reveal>

      <Reveal delay={0.1}>
        <div className="mt-10 rounded-xl border border-border bg-sand-muted/50 p-6 sm:p-8">
          <h2 className="text-xs font-mono uppercase tracking-[0.2em] font-semibold text-muted-foreground">
            {a.legendTitle}
          </h2>
          <ul className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-5">
            {a.legend.map((item) => (
              <li key={item.status} className="flex flex-col gap-2">
                <MaturityBadge status={item.status} testId={`legend-badge-${item.status}`} />
                <p className="text-sm text-muted-foreground leading-relaxed">{item.text}</p>
              </li>
            ))}
          </ul>
        </div>
      </Reveal>

      <div className="mt-12 grid grid-cols-1 md:grid-cols-12 gap-6 sm:gap-8">
        {published.map((app) => (
          <Reveal key={app.slug} className="md:col-span-8 lg:col-span-7">
            <article
              data-testid="app-card-learnova"
              className="h-full rounded-2xl border-2 border-ochre/30 bg-card p-8 sm:p-10 transition-all duration-300 hover:-translate-y-1 hover:border-ochre/60 hover:shadow-lg"
            >
              <div className="flex flex-wrap items-center justify-between gap-3">
                <span className="text-xs font-mono uppercase tracking-[0.2em] font-semibold text-ochre">
                  {a.flagshipLabel}
                </span>
                <MaturityBadge status={app.status} testId="app-card-learnova-badge" />
              </div>
              <h2 className="mt-5 font-display text-3xl sm:text-4xl font-extrabold tracking-tight">{app.name}</h2>
              <p className="mt-2 text-xs font-mono uppercase tracking-[0.16em] text-muted-foreground">
                {a.domainLabel} — {a.domain}
              </p>
              <p className="mt-4 text-muted-foreground leading-relaxed">{a.learnovaDesc}</p>
              <div className="mt-7 flex flex-wrap gap-4">
                <Link
                  to={app.path}
                  data-testid="app-card-learnova-more"
                  className="inline-flex items-center gap-2 rounded-full bg-ink px-5 py-2.5 text-sm font-semibold text-white transition-all duration-200 hover:bg-ochre active:scale-[0.98]"
                >
                  {t.common.learnMore}
                  <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </Link>
                <a
                  href={app.externalUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="app-card-learnova-prototype"
                  className="inline-flex items-center gap-2 rounded-full border border-ink/25 px-5 py-2.5 text-sm font-semibold text-ink transition-all duration-200 hover:border-ochre hover:text-ochre active:scale-[0.98]"
                >
                  {a.ctaPrototype}
                  <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
                </a>
              </div>
            </article>
          </Reveal>
        ))}
        <Reveal delay={0.1} className="md:col-span-4 lg:col-span-5">
          <div className="h-full rounded-2xl border border-dashed border-border bg-sand-muted/40 p-8 sm:p-10 flex flex-col justify-center">
            <h2 className="font-display text-xl font-bold tracking-tight">{a.moreTitle}</h2>
            <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{a.moreText}</p>
          </div>
        </Reveal>
      </div>
    </div>
  );
}
